import { useState } from 'react';
import {
  AlertTriangle,
  Building2,
  CheckCircle,
  DollarSign,
  FileSpreadsheet,
  MoreHorizontal,
  Plus,
  ShieldAlert,
  Trash2,
  UserCheck,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  centsToNumber,
  exportToXlsx,
  isoToBrDate,
} from '@/lib/excel-export';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Skeleton } from '@/components/ui/skeleton';
import { extractErrorMessage } from '@/lib/api-client';
import { formatDate } from '@/lib/format';
import { centsToReais } from '@/features/fueling/types';
import {
  useCancelFine,
  useDeleteFine,
  useFinesList,
  useFinesSummary,
  usePayFine,
  useTakeFineResponsibility,
} from './api';
import {
  SEVERITY_LABELS,
  SEVERITY_VARIANT,
  STATUS_LABELS,
  STATUS_VARIANT,
  type Fine,
} from './types';
import { FineFormDialog } from './fine-form-dialog';
import { IndicateDriverDialog } from './indicate-driver-dialog';

export function FinesPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [indicateTarget, setIndicateTarget] = useState<Fine | null>(null);

  const listQuery = useFinesList({ page: 1, limit: 50, sort: '-infractionDate' });
  const summaryQuery = useFinesSummary();
  const deleteFine = useDeleteFine();
  const payFine = usePayFine();
  const cancelFine = useCancelFine();
  const takeResponsibility = useTakeFineResponsibility();

  const handleTakeResponsibility = async (id: string) => {
    try {
      await takeResponsibility.mutateAsync(id);
      toast.success('Empresa assumiu a responsabilidade');
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const fines = listQuery.data?.data ?? [];
  const summary = summaryQuery.data as Record<string, unknown> | undefined;

  const handleExportXlsx = () => {
    if (fines.length === 0) {
      toast.error('Nenhuma multa para exportar.');
      return;
    }
    const rows = fines.map((f) => ({
      Data: isoToBrDate(f.infractionDate),
      Veículo: f.vehicle?.plate ?? '',
      Marca: f.vehicle?.brand ?? '',
      Modelo: f.vehicle?.model ?? '',
      Descrição: f.description ?? '',
      Gravidade: SEVERITY_LABELS[f.severity] ?? f.severity,
      Pontos: f.points,
      Valor: centsToNumber(f.amountCents),
      'Valor c/ Desconto': f.discountAmountCents ? centsToNumber(f.discountAmountCents) : '',
      'Valor Pago': f.paidAmountCents ? centsToNumber(f.paidAmountCents) : '',
      Vencimento: isoToBrDate(f.dueDate),
      'Data Pagamento': isoToBrDate(f.paidAt),
      Responsável: f.indicatedDriver?.name ?? f.driver?.name ?? 'Empresa',
      Indicado: f.indicatedDriver ? 'Sim' : 'Não',
      Status: STATUS_LABELS[f.status] ?? f.status,
      Local: f.location ?? '',
      'Cód. Infração': f.infractionCode ?? '',
      AIT: f.autoInfractionId ?? '',
      Observações: f.notes ?? '',
    }));
    exportToXlsx(rows, {
      filename: 'frota-multas',
      sheetName: 'Multas',
      columnWidths: [12, 10, 12, 16, 30, 12, 8, 12, 14, 12, 12, 14, 22, 10, 14, 24, 14, 14, 28],
    });
    toast.success(`${fines.length} multas exportadas`);
  };

  const handlePay = async (fine: Fine) => {
    try {
      await payFine.mutateAsync({ id: fine.id, paidAmountCents: fine.discountAmountCents ?? fine.amountCents });
      toast.success('Multa marcada como paga');
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  const handleCancel = async (id: string) => {
    try {
      await cancelFine.mutateAsync(id);
      toast.success('Multa cancelada');
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteFine.mutateAsync(id);
      toast.success('Multa removida');
    } catch (err) { toast.error(extractErrorMessage(err)); }
  };

  return (
    <div className="page">
      <header className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="space-y-1">
          <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            <ShieldAlert className="size-3.5" /> Operação
          </div>
          <h1 className="text-2xl font-semibold tracking-tight md:text-3xl">Multas</h1>
          <p className="text-sm text-muted-foreground">Gestão de multas, indicação de condutor e pagamentos.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportXlsx}>
            <FileSpreadsheet className="size-4" />
            Exportar Excel
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" />
            Nova multa
          </Button>
        </div>
      </header>

      {/* KPIs */}
      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard title="Total de multas" value={String(summary.totalFines ?? 0)} icon={<ShieldAlert className="size-5" />} color="text-primary bg-primary/10" />
          <KpiCard title="Valor total" value={centsToReais(Number(summary.totalAmountCents ?? 0))} icon={<DollarSign className="size-5" />} color="text-destructive bg-destructive/10" />
          <KpiCard title="Pendentes" value={String(summary.pendingCount ?? 0)} icon={<AlertTriangle className="size-5" />} color="text-warning bg-warning/10" />
          <KpiCard title="Pagas" value={String(summary.paidCount ?? 0)} icon={<CheckCircle className="size-5" />} color="text-success bg-success/10" />
        </div>
      )}

      <FineFormDialog open={createOpen} onOpenChange={setCreateOpen} />
      <IndicateDriverDialog
        open={!!indicateTarget}
        onOpenChange={(o) => !o && setIndicateTarget(null)}
        fine={indicateTarget}
      />

      <Card>
        <CardContent className="p-0">
          {listQuery.isLoading ? (
            <div className="space-y-3 p-4">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div>
          ) : fines.length === 0 ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <ShieldAlert className="size-10 text-muted-foreground" />
              <p className="text-sm font-medium">Nenhuma multa registrada</p>
              <p className="text-xs text-muted-foreground">
                Clique em "Nova multa" para cadastrar um auto de infração.
              </p>
              <Button onClick={() => setCreateOpen(true)} className="mt-2">
                <Plus className="size-4" />
                Nova multa
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="px-4 py-3 font-medium">Data</th>
                    <th className="px-4 py-3 font-medium">Veículo</th>
                    <th className="px-4 py-3 font-medium">Descrição</th>
                    <th className="px-4 py-3 font-medium">Gravidade</th>
                    <th className="px-4 py-3 font-medium text-center">Pontos</th>
                    <th className="px-4 py-3 font-medium text-right">Valor</th>
                    <th className="px-4 py-3 font-medium">Vencimento</th>
                    <th className="px-4 py-3 font-medium">Responsável</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="w-10 px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {fines.map((f) => {
                    const isOverdue = f.dueDate && new Date(f.dueDate) < new Date() && f.status === 'pending';
                    return (
                      <tr key={f.id} className="transition-colors hover:bg-muted/30">
                        <td className="px-4 py-3 text-xs">{formatDate(f.infractionDate)}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs font-semibold">{f.vehicle?.plate ?? '—'}</span>
                        </td>
                        <td className="px-4 py-3 text-xs max-w-[260px] whitespace-normal break-words">{f.description}</td>
                        <td className="px-4 py-3">
                          <Badge variant={SEVERITY_VARIANT[f.severity]}>{SEVERITY_LABELS[f.severity]}</Badge>
                        </td>
                        <td className="px-4 py-3 text-center font-mono text-xs">{f.points}</td>
                        <td className="px-4 py-3 text-right font-mono text-xs font-medium tabular-nums">
                          {centsToReais(f.amountCents)}
                        </td>
                        <td className="px-4 py-3 text-xs">
                          {f.dueDate ? (
                            <span className={isOverdue ? 'font-medium text-destructive' : 'text-muted-foreground'}>
                              {formatDate(f.dueDate)} {isOverdue && '⚠️'}
                            </span>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs max-w-[220px]">
                          {f.indicatedDriver ? (
                            <span className="flex items-start gap-1">
                              <UserCheck className="mt-0.5 size-3 shrink-0 text-success" />
                              <span className="min-w-0 break-words">
                                <span className="font-medium">{f.indicatedDriver.name}</span>{' '}
                                <Badge variant="outline" className="text-[10px]">indicado</Badge>
                              </span>
                            </span>
                          ) : f.driver ? (
                            <span className="flex items-start gap-1">
                              <UserCheck className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
                              <span className="min-w-0 break-words">{f.driver.name}</span>
                            </span>
                          ) : (
                            <span className="flex items-start gap-1 text-muted-foreground">
                              <Building2 className="mt-0.5 size-3 shrink-0" />
                              Empresa
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={STATUS_VARIANT[f.status]}>{STATUS_LABELS[f.status]}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="size-8">
                                <MoreHorizontal className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {(f.status === 'pending' || f.status === 'driver_indicated') && (
                                <>
                                  <DropdownMenuItem onClick={() => setIndicateTarget(f)}>
                                    <UserCheck className="size-4" />
                                    {f.indicatedDriver ? 'Alterar indicação' : 'Indicar condutor'}
                                  </DropdownMenuItem>
                                  {f.indicatedDriver && (
                                    <DropdownMenuItem onClick={() => handleTakeResponsibility(f.id)}>
                                      <Building2 className="size-4" />
                                      Empresa assume
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              {f.status === 'pending' && (
                                <>
                                  <DropdownMenuItem onClick={() => handlePay(f)}>
                                    <DollarSign className="size-4" /> Marcar como paga
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => handleCancel(f.id)}>
                                    <XCircle className="size-4" /> Cancelar
                                  </DropdownMenuItem>
                                </>
                              )}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => handleDelete(f.id)} className="text-destructive">
                                <Trash2 className="size-4" /> Remover
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function KpiCard({ title, value, icon, color }: { title: string; value: string; icon: React.ReactNode; color: string }) {
  return (
    <Card>
      <CardContent className="flex items-start justify-between p-5">
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{title}</p>
          <p className="font-mono text-2xl font-semibold tabular-nums">{value}</p>
        </div>
        <div className={`flex size-10 items-center justify-center rounded-lg ${color}`}>{icon}</div>
      </CardContent>
    </Card>
  );
}
